/**
 * @fileoverview Component for individual process items in the sequence
 * Each item can be dragged to reorder or removed from the sequence
 */

import React, { useRef, useState } from 'react';
import { ListGroupItem, Badge, Button, ButtonGroup, Input } from 'reactstrap';
import { CentroCostoPeso } from '../types/collections/CentroCostoPeso';
import './ProcesoItem.css';

/**
 * Props for ProcesoItem component
 */
export interface ProcesoItemProps {
  /** Cost center with weight object */
  centro: CentroCostoPeso;
  /** Index in the sequence */
  index: number;
  /** Callback when item is removed */
  onRemove: (index: number) => void;
  /** Callback when items are reordered */
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Callback when weight is changed */
  onWeightChange?: (index: number, weight: number) => void;
  /** Whether the component is in read-only mode */
  readOnly?: boolean;
  /** Whether this is the last item in the sequence */
  isLast?: boolean;
}

/**
 * Component for individual process items in the sequence
 * Supports drag & drop reordering and removal
 */
export const ProcesoItem: React.FC<ProcesoItemProps> = ({
  centro,
  index,
  onRemove,
  onReorder,
  onWeightChange,
  readOnly = false,
  isLast = false
}) => {
  // No necesitamos el ref para el ListGroupItem
  // const itemRef = useRef<HTMLDivElement>(null);
  const [weight, setWeight] = useState<number>(centro.peso || 0);

  /**
   * Handle drag start for reordering
   * @param e - Drag event
   */
  const handleDragStart = (e: React.DragEvent) => {
    if (readOnly) return;
    
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.currentTarget.classList.add('dragging');
  };

  /**
   * Handle drag end
   * @param e - Drag event
   */
  const handleDragEnd = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('dragging');
  };

  /**
   * Handle drop for reordering
   * @param e - Drop event
   */
  const handleDrop = (e: React.DragEvent) => {
    if (readOnly) return;
    
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (!isNaN(fromIndex) && fromIndex !== index) {
      onReorder(fromIndex, index);
    }
  };

  /**
   * Handle drag over to allow dropping
   * @param e - Drag over event
   */
  const handleDragOver = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  /**
   * Handle drag leave
   * @param e - Drag leave event
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  };

  /**
   * Handle drag enter
   * @param e - Drag enter event
   */
  const handleDragEnter = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  /**
   * Get machine name for display
   * @param centro - Cost center with weight object
   * @returns Machine name or "Sin máquina"
   */
  const getMachineName = (centro: CentroCostoPeso): string => {
    try {
      // Caso 1: Si no hay expand o está vacío, buscar el centro por ID
      if (!centro.expand || !centro.centro_costo || Object.keys(centro.expand).length === 0) {
        // Aquí debemos buscar el centro de costo por ID en los datos disponibles
        // Como no tenemos acceso directo a los datos, mostramos un placeholder
        return 'Sin máquina';
      }
      
      // Caso 2: Si hay expand pero no tiene centro_costo
      if (!centro.expand.centro_costo) {
        return 'Sin máquina';
      }
      
      const centroCosto = centro.expand.centro_costo;
      
      // Caso 3: Si centroCosto es un objeto y tiene maquina expandido
      if (typeof centroCosto === 'object' && centroCosto !== null) {
        // Si maquina es un objeto con nombre en expand
        if ((centroCosto as any).expand?.maquina?.nombre) {
          return (centroCosto as any).expand.maquina.nombre;
        }
        
        // Si maquina es un objeto directo
        if (typeof centroCosto.maquina === 'object' && 'nombre' in (centroCosto.maquina as any)) {
          return (centroCosto.maquina as any).nombre;
        }
        
        // Si maquina es un ID y tenemos el nombre en algún lugar
        if (centroCosto.maquina && typeof centroCosto.maquina === 'string') {
          // Buscar la máquina en los datos expandidos del centro de costo
          if ((centroCosto as any).expand?.maquina?.nombre) {
            return (centroCosto as any).expand.maquina.nombre;
          }
          // Si no está expandido, mostrar el ID
          return `Máquina ${centroCosto.maquina}`;
        }
      }
    } catch (e) {
      console.error('Error getting machine name:', e);
    }
    
    return 'Sin máquina';
  };

  /**
   * Get user name for display
   * @param centro - Cost center with weight object
   * @returns User name or "Sin encargado"
   */
  const getUserName = (centro: CentroCostoPeso): string => {
    try {
      // Caso 1: Si no hay expand o está vacío
      if (!centro.expand || !centro.centro_costo || Object.keys(centro.expand).length === 0) {
        return 'Sin encargado';
      }
      
      // Caso 2: Si hay expand pero no tiene centro_costo
      if (!centro.expand.centro_costo) {
        return 'Sin encargado';
      }
      
      const centroCosto = centro.expand.centro_costo;
      
      // Caso 3: Si centroCosto es un objeto y tiene encargado expandido
      if (typeof centroCosto === 'object' && centroCosto !== null) {
        // Si encargado es un objeto con name en expand
        if ((centroCosto as any).expand?.encargado?.name) {
          return (centroCosto as any).expand.encargado.name;
        }
        
        // Si encargado es un objeto directo
        if (typeof centroCosto.encargado === 'object' && 'name' in (centroCosto.encargado as any)) {
          return (centroCosto.encargado as any).name;
        }
        
        // Si encargado es un ID
        if (centroCosto.encargado && typeof centroCosto.encargado === 'string') {
          // Buscar el encargado en los datos expandidos del centro de costo
          if ((centroCosto as any).expand?.encargado?.name) {
            return (centroCosto as any).expand.encargado.name;
          }
          return `Usuario ${centroCosto.encargado}`;
        }
      }
    } catch (e) {
      console.error('Error getting user name:', e);
    }
    
    return 'Sin encargado';
  };

  /**
   * Get process name for display
   * @param centro - Cost center with weight object
   * @returns Process name or empty string
   */
  const getProcessName = (centro: CentroCostoPeso): string => {
    try {
      // Verificar si hay datos expandidos del centro de costo
      if (!centro.expand?.centro_costo) return '';
      
      const centroCosto = centro.expand.centro_costo;
      
      // Si centroCosto es un objeto y tiene proceso expandido
      if (typeof centroCosto === 'object' && centroCosto !== null) {
        // Si proceso es un objeto con nombre
        if ((centroCosto as any).expand?.proceso?.nombre) {
          return (centroCosto as any).expand.proceso.nombre;
        }
        
        // Si proceso es un objeto directo
        if (typeof centroCosto.proceso === 'object' && 'nombre' in (centroCosto.proceso as any)) {
          return (centroCosto.proceso as any).nombre;
        }
      }
    } catch (e) {
      console.error('Error getting process name:', e);
    }
    
    return '';
  };

  /**
   * Get cost center name for display
   * @param centro - Cost center with weight object
   * @returns Cost center name or empty string
   */
  const getCentroName = (centro: CentroCostoPeso): string => {
    try {
      // Verificar si hay datos expandidos del centro de costo
      if (!centro.expand?.centro_costo) return 'Centro sin nombre';
      
      const centroCosto = centro.expand.centro_costo;
      
      // Si centroCosto es un objeto con nombre
      if (typeof centroCosto === 'object' && centroCosto !== null && 'nombre' in centroCosto) {
        return centroCosto.nombre as string;
      }
    } catch (e) {
      console.error('Error getting centro name:', e);
    }
    
    // Si no hay nombre, usar ID o placeholder
    return centro.centro_costo || 'Centro sin nombre';
  };

  /**
   * Handle remove button click
   */
  const handleRemove = () => {
    if (readOnly) return;
    onRemove(index);
  };

  /**
   * Handle weight input change
   * @param e - Input change event
   */
  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    
    const newWeight = parseInt(e.target.value) || 0;
    setWeight(newWeight);
    
    if (onWeightChange) {
      onWeightChange(index, newWeight);
    }
  };

  return (
    <ListGroupItem
      id={`proceso-item-${centro.id}`}
      className={`proceso-item ${readOnly ? 'disabled' : 'draggable'}`}
      draggable={!readOnly}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnter={handleDragEnter}
      style={{ cursor: readOnly ? 'default' : 'grab' }}
      title={readOnly ? 'Modo solo lectura' : 'Arrastra para reordenar'}
    >
      {/* Header Row - Order, Title, and Close Button */}
      <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
        {/* Order Badge */}
        <Badge 
          color="primary" 
          className="order-badge"
          style={{ minWidth: '2rem', fontSize: '0.75rem', flexShrink: 0 }}
        >
          {index + 1}
        </Badge>
        
        {/* Title - Full width minus badge and close button */}
        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
          <span className="fw-bold text-truncate me-2">{getCentroName(centro)}</span>
          {!readOnly && (
            <i className="fas fa-grip-vertical text-muted"></i>
          )}
        </div>
        
        {/* Close Button */}
        <div className="d-flex justify-content-end" style={{ flexShrink: 0 }}>
          {!readOnly && (
            <Button
              color="outline-danger"
              size="sm"
              onClick={handleRemove}
              title="Eliminar de la secuencia"
              className="btn-sm"
              style={{ minWidth: '28px', height: '28px' }}
            >
              <i className="fas fa-times"></i>
            </Button>
          )}
        </div>
      </div>

      {/* Weight Row - Below the name */}
      <div className="d-flex align-items-center justify-content-center mb-2">
        <small className="text-muted me-2">
          <i className="fas fa-weight me-1"></i>
          Peso:
        </small>
        <Input
          id={`peso-input-${centro.id}`}
          type="number"
          min="0"
          max="100"
          value={weight}
          onChange={handleWeightChange}
          onFocus={(e) => {
            // Select all text when focused so typing replaces it
            e.target.select();
          }}
          disabled={readOnly}
          className="form-control form-control-sm text-center"
          style={{ 
            width: '90px',
            fontSize: '0.9rem',
            minWidth: '80px'
          }}
          placeholder="0"
        />
        <small className="text-muted ms-1">%</small>
      </div>

      {/* Details Row - 3 fields in 33.33% each */}
      <div className="d-flex align-items-center" style={{ gap: '8px' }}>
        {/* Machine - 33.33% */}
        <div className="d-flex align-items-center" style={{ width: '33.33%', minWidth: 0 }}>
          <small className="text-muted d-flex align-items-center w-100">
            <i className="fas fa-cog me-1 flex-shrink-0"></i>
            <span 
              className="text-truncate" 
              title={getMachineName(centro)}
              style={{ 
                maxWidth: 'calc(100% - 20px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getMachineName(centro)}
            </span>
          </small>
        </div>
        
        {/* User - 33.33% */}
        <div className="d-flex align-items-center" style={{ width: '33.33%', minWidth: 0 }}>
          <small className="text-muted d-flex align-items-center w-100">
            <i className="fas fa-user me-1 flex-shrink-0"></i>
            <span 
              className="text-truncate" 
              title={getUserName(centro)}
              style={{ 
                maxWidth: 'calc(100% - 20px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getUserName(centro)}
            </span>
          </small>
        </div>
        
        {/* Process - 33.33% */}
        <div className="d-flex align-items-center" style={{ width: '33.33%', minWidth: 0 }}>
          <small className="text-muted d-flex align-items-center w-100">
            <i className="fas fa-cogs me-1 flex-shrink-0"></i>
            <span 
              className="text-truncate" 
              title={getProcessName(centro) || "Sin proceso"}
              style={{ 
                maxWidth: 'calc(100% - 20px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {getProcessName(centro) || "Sin proceso"}
            </span>
          </small>
        </div>
      </div>

      {/* Drag indicator row - centered */}
      {!readOnly && (
        <div className="d-flex justify-content-center mt-1">
          <div 
            className="drag-handle"
            style={{
              padding: '4px 8px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'grab',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e9ecef';
              e.currentTarget.style.borderColor = '#adb5bd';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.borderColor = '#dee2e6';
            }}
          >
            <small className="text-muted mb-0" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
              <i className="fas fa-arrows-alt me-1"></i>
              Mover
            </small>
          </div>
        </div>
      )}
    </ListGroupItem>
  );
};


